import { SIZE } from 'baseui/input';
import { ALIGN, Radio, RadioGroup } from 'baseui/radio';
import React, { FC } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { setContactDetails } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import store from 'store';
import { FormValidator } from 'tools/FormValidator';
import { RadioOptionKeys, translationKeyForRadioOption } from 'tools/RadioOptionMapper';
import { Button } from '@techem/ui-components';
import FormControl from 'presentation/components/FormControl';
import { Controller, useForm } from 'react-hook-form';
import Input from 'presentation/components/Input';
import { KIND } from 'baseui/button';

interface EditContactDetailsViewProperties {
  callbackOnSuccess: () => void;
  callbackOnAbort: () => void;
}
interface FormData {
  gender: string;
  fname: string;
  lname: string;
  phone: string;
  email: string;
}
const EditContactDetailsView: FC<EditContactDetailsViewProperties> = (props) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    formState: { errors },
    control
  } = useForm<FormData>({
    defaultValues: store.getState().onboardingInfo.contactDetails
  });

  const buildEMailErrorMessage = () => {
    switch (errors?.email?.type) {
      case 'required':
        return t('pageContactDetailsErrorEMailAddressEmpty');
      case 'asciiOnly':
        return t('pageContactDetailsErrorEMailAddressNonAscii');
      case 'format':
        return t('pageContactDetailsErrorEMailAddressInvalid');
      default:
        return null;
    }
  };

  const onSubmit = handleSubmit(({ gender, fname, lname, phone, email }, e) => {
    e?.preventDefault();
    store.dispatch(
      setContactDetails({
        gender,
        fname,
        lname,
        phone,
        email
      })
    );
    props.callbackOnSuccess();
  });

  return (
    <Col xs={12} className="my-4">
      <form
        noValidate
        onSubmit={onSubmit}
        aria-labelledby="edit-contact-details-title"
        data-gi="edit-contact-details-form"
      >
        <h2 id="edit-contact-details-title" className="visually-hidden">
          {t('summaryEditContactDetailsTitle')}
        </h2>
        <Row>
          <Col xs={12} style={{ marginBottom: '-8px' }}>
            <FormControl
              required
              label={() => (
                <label htmlFor={'input-gender'}>{t('pageContactDetailsLabelGender')}</label>
              )}
              error={errors.gender && t('pageContactDetailsErrorGender')}
            >
              <>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <RadioGroup
                      id={'input-gender'}
                      value={field.value}
                      name={field.name}
                      onChange={(e) => field.onChange(e.target.value)}
                      align={ALIGN.horizontal}
                      aria-invalid={!!errors.gender}
                      aria-describedby={errors.gender ? 'gender-error' : undefined}
                    >
                      <Radio
                        value={RadioOptionKeys.ContactOptions.Female}
                        checked={field.value === RadioOptionKeys.ContactOptions.Female}
                        overrides={{
                          Label: () => (
                            <span className="ms-2 me-5">
                              {t(
                                translationKeyForRadioOption(RadioOptionKeys.ContactOptions.Female)
                              )}
                            </span>
                          )
                        }}
                      ></Radio>
                      <Radio
                        value={RadioOptionKeys.ContactOptions.Male}
                        checked={field.value === RadioOptionKeys.ContactOptions.Male}
                        overrides={{
                          Label: () => (
                            <span className="ms-2 me-5">
                              {t(translationKeyForRadioOption(RadioOptionKeys.ContactOptions.Male))}
                            </span>
                          )
                        }}
                      ></Radio>
                    </RadioGroup>
                  )}
                />
                {errors.gender && (
                  <span id="gender-error" className="visually-hidden" aria-live="polite">
                    {t('pageContactDetailsErrorGender')}
                  </span>
                )}
              </>
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="fname-input">{t('pageContactDetailsLabelFirstName')}</label>
              )}
              error={errors.fname && t('pageContactDetailsErrorFirstName')}
            >
              <>
                <Controller
                  name="fname"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      validate: (v) => FormValidator.isValidFirstOrLastName(v)
                    }
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id="fname-input"
                      type="text"
                      size={SIZE.default}
                      error={!!errors.fname}
                      value={value}
                      onChange={onChange}
                      autoComplete={'given-name'}
                      additionalTestId="firstName"
                      aria-required={true}
                      aria-invalid={!!errors.fname}
                      aria-describedby={errors.fname ? 'fname-error' : undefined}
                    />
                  )}
                />
                {errors.fname && (
                  <span id="fname-error" className="visually-hidden" aria-live="polite">
                    {t('pageContactDetailsErrorFirstName')}
                  </span>
                )}
              </>
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="lname-input">{t('pageContactDetailsLabelLastName')}</label>
              )}
              error={errors.lname && t('pageContactDetailsErrorLastName')}
            >
              <>
                <Controller
                  name="lname"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      validate: (v) => FormValidator.isValidFirstOrLastName(v)
                    }
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id="lname-input"
                      type="text"
                      size={SIZE.default}
                      error={!!errors.lname}
                      value={value}
                      onChange={onChange}
                      autoComplete={'family-name'}
                      additionalTestId="lastName"
                      aria-required={true}
                      aria-invalid={!!errors.lname}
                      aria-describedby={errors.lname ? 'lname-error' : undefined}
                    />
                  )}
                />
                {errors.lname && (
                  <span id="lname-error" className="visually-hidden" aria-live="polite">
                    {t('pageContactDetailsErrorLastName')}
                  </span>
                )}
              </>
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="phone-input">{t('pageContactDetailsLabelPhoneNumber')}</label>
              )}
              error={errors.phone && t('pageContactDetailsErrorPhoneNumber')}
            >
              <>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      validate: (v) => FormValidator.isValidPhoneNumber(v)
                    }
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id="phone-input"
                      size={SIZE.default}
                      error={!!errors.phone}
                      type="tel"
                      value={value}
                      onChange={onChange}
                      autoComplete={'tel'}
                      additionalTestId="phoneNumber"
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                    />
                  )}
                />
                {errors.phone && (
                  <span id="phone-error" className="visually-hidden" aria-live="polite">
                    {t('pageContactDetailsErrorPhoneNumber')}
                  </span>
                )}
              </>
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="email-input">{t('pageContactDetailsLabelEMailAddress')}</label>
              )}
              error={buildEMailErrorMessage()}
            >
              <>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      asciiOnly: (v) => FormValidator.containsAsciiCharsOnly(v),
                      format: (v) => FormValidator.isValidMailAddress(v)
                    }
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id="email-input"
                      size={SIZE.default}
                      type="email"
                      error={!!errors.email}
                      value={value}
                      onChange={onChange}
                      autoComplete={'email'}
                      additionalTestId="emailAddress"
                      aria-required={true}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                  )}
                />
                {errors.email && (
                  <span id="email-error" className="visually-hidden" aria-live="polite">
                    {buildEMailErrorMessage()}
                  </span>
                )}
              </>
            </FormControl>
          </Col>

          <Col xs={12} md="auto">
            <Row className="justify-content-center">
              <Col xs="auto" className="my-1 order-sm-last order-md-first text-center">
                <Button size={SIZE.large} type="submit">
                  {t('summaryEditSaveButtonText')}
                </Button>
              </Col>
              <Col xs={12} sm="auto" className="my-1 text-center">
                <Button
                  kind={KIND.secondary}
                  size={SIZE.large}
                  onClick={() => {
                    props.callbackOnAbort();
                  }}
                >
                  {t('summaryEditAbortButtonText')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </Col>
  );
};

export default EditContactDetailsView;
